/**
 * TriageOnlyDialog Component
 * Dialog for registering triage (vital signs) without starting attendance
 *
 * @version 2025.1.0
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  InputAdornment,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  LocalHospital as TriageIcon,
} from '@mui/icons-material';
import { ReceptionAppointment } from '../../../types/reception';
import {
  TriageData,
  INITIAL_TRIAGE_DATA,
  TRIAGE_VALIDATION,
  calculateBMI,
  getBMIClassification,
  hasTriageData,
} from '../../../types/triage';

interface TriageOnlyDialogProps {
  open: boolean;
  appointment: ReceptionAppointment | null;
  loading: boolean;
  onConfirm: (triageData: TriageData) => void;
  onClose: () => void;
}

export const TriageOnlyDialog: React.FC<TriageOnlyDialogProps> = ({
  open,
  appointment,
  loading,
  onConfirm,
  onClose,
}) => {
  const [triageData, setTriageData] = useState<TriageData>(INITIAL_TRIAGE_DATA);

  // Reset form when dialog opens with new appointment
  React.useEffect(() => {
    if (open) {
      setTriageData(INITIAL_TRIAGE_DATA);
    }
  }, [open, appointment?.id]);

  const handleFieldChange = useCallback((field: keyof TriageData, value: string) => {
    const numericFields = [
      'bloodPressureSystolic',
      'bloodPressureDiastolic',
      'heartRate',
      'temperature',
      'weight',
      'height',
      'oxygenSaturation',
    ];

    if (numericFields.includes(field)) {
      const numValue = value === '' ? null : parseFloat(value);
      setTriageData(prev => ({ ...prev, [field]: numValue }));
    } else {
      setTriageData(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  const getFieldError = (field: keyof typeof TRIAGE_VALIDATION, value: number | null): string | null => {
    if (value === null) return null;
    const validation = TRIAGE_VALIDATION[field];
    if (value < validation.min || value > validation.max) {
      return `Valor deve estar entre ${validation.min} e ${validation.max}`;
    }
    return null;
  };

  const handleConfirm = () => {
    onConfirm(triageData);
  };

  const bmi = calculateBMI(triageData.weight, triageData.height);
  const bmiClassification = getBMIClassification(bmi);

  // Check if any data has been filled
  const hasData = hasTriageData(triageData);

  if (!appointment) return null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <TriageIcon color="secondary" />
          <Typography variant="h6">Registrar Triagem</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Patient Info */}
        <Box mb={3} p={2} bgcolor="action.hover" borderRadius={1}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Paciente
          </Typography>
          <Typography variant="body1" fontWeight="bold">
            {appointment.employeeName}
          </Typography>
          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
            <Chip label={appointment.companyName} size="small" variant="outlined" />
            <Chip label={appointment.examType} size="small" color="primary" variant="outlined" />
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Preencha os sinais vitais do paciente. Ao salvar, o status será alterado para "Triagem Feita".
        </Typography>

        <Grid container spacing={2}>
          {/* Blood Pressure */}
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pressão Arterial
            </Typography>
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              size="small"
              label="Sistólica"
              type="number"
              value={triageData.bloodPressureSystolic ?? ''}
              onChange={(e) => handleFieldChange('bloodPressureSystolic', e.target.value)}
              error={!!getFieldError('bloodPressureSystolic', triageData.bloodPressureSystolic)}
              helperText={getFieldError('bloodPressureSystolic', triageData.bloodPressureSystolic)}
              InputProps={{
                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
              }}
              inputProps={{ min: 60, max: 250 }}
              disabled={loading}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              size="small"
              label="Diastólica"
              type="number"
              value={triageData.bloodPressureDiastolic ?? ''}
              onChange={(e) => handleFieldChange('bloodPressureDiastolic', e.target.value)}
              error={!!getFieldError('bloodPressureDiastolic', triageData.bloodPressureDiastolic)}
              helperText={getFieldError('bloodPressureDiastolic', triageData.bloodPressureDiastolic)}
              InputProps={{
                endAdornment: <InputAdornment position="end">mmHg</InputAdornment>,
              }}
              inputProps={{ min: 40, max: 150 }}
              disabled={loading}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Heart Rate and Temperature */}
          <Grid size={6}>
            <TextField
              fullWidth
              size="small"
              label="Frequência Cardíaca"
              type="number"
              value={triageData.heartRate ?? ''}
              onChange={(e) => handleFieldChange('heartRate', e.target.value)}
              error={!!getFieldError('heartRate', triageData.heartRate)}
              helperText={getFieldError('heartRate', triageData.heartRate)}
              InputProps={{
                endAdornment: <InputAdornment position="end">bpm</InputAdornment>,
              }}
              inputProps={{ min: 30, max: 220 }}
              disabled={loading}
            />
          </Grid>
          <Grid size={6}>
            <TextField
              fullWidth
              size="small"
              label="Temperatura"
              type="number"
              value={triageData.temperature ?? ''}
              onChange={(e) => handleFieldChange('temperature', e.target.value)}
              error={!!getFieldError('temperature', triageData.temperature)}
              helperText={getFieldError('temperature', triageData.temperature)}
              InputProps={{
                endAdornment: <InputAdornment position="end">°C</InputAdornment>,
              }}
              inputProps={{ min: 32, max: 42, step: 0.1 }}
              disabled={loading}
            />
          </Grid>

          {/* Oxygen Saturation */}
          <Grid size={6}>
            <TextField
              fullWidth
              size="small"
              label="Saturação O2"
              type="number"
              value={triageData.oxygenSaturation ?? ''}
              onChange={(e) => handleFieldChange('oxygenSaturation', e.target.value)}
              error={!!getFieldError('oxygenSaturation', triageData.oxygenSaturation)}
              helperText={getFieldError('oxygenSaturation', triageData.oxygenSaturation)}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              inputProps={{ min: 70, max: 100 }}
              disabled={loading}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Weight and Height */}
          <Grid size={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Medidas Antropometricas
            </Typography>
          </Grid>
          <Grid size={4}>
            <TextField
              fullWidth
              size="small"
              label="Peso"
              type="number"
              value={triageData.weight ?? ''}
              onChange={(e) => handleFieldChange('weight', e.target.value)}
              error={!!getFieldError('weight', triageData.weight)}
              helperText={getFieldError('weight', triageData.weight)}
              InputProps={{
                endAdornment: <InputAdornment position="end">kg</InputAdornment>,
              }}
              inputProps={{ min: 20, max: 300, step: 0.1 }}
              disabled={loading}
            />
          </Grid>
          <Grid size={4}>
            <TextField
              fullWidth
              size="small"
              label="Altura"
              type="number"
              value={triageData.height ?? ''}
              onChange={(e) => handleFieldChange('height', e.target.value)}
              error={!!getFieldError('height', triageData.height)}
              helperText={getFieldError('height', triageData.height)}
              InputProps={{
                endAdornment: <InputAdornment position="end">cm</InputAdornment>,
              }}
              inputProps={{ min: 100, max: 250 }}
              disabled={loading}
            />
          </Grid>
          <Grid size={4}>
            <TextField
              fullWidth
              size="small"
              label="IMC"
              value={bmi !== null ? `${bmi} - ${bmiClassification}` : '-'}
              disabled
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>

          <Grid size={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* Observations */}
          <Grid size={12}>
            <TextField
              fullWidth
              size="small"
              label="Observacoes"
              multiline
              rows={3}
              value={triageData.observations}
              onChange={(e) => handleFieldChange('observations', e.target.value)}
              placeholder="Observacoes adicionais sobre o estado do paciente..."
              disabled={loading}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleConfirm}
          disabled={loading || !hasData}
          startIcon={loading ? <CircularProgress size={20} /> : <TriageIcon />}
        >
          {loading ? 'Salvando...' : 'Registrar Triagem'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriageOnlyDialog;
